import React, { useState } from 'react';
import dayjs from 'dayjs';
// Hooks
import { useAppDispatch } from 'hooks/redux';
// Async
import SubscriptionsAsync from 'store/subscriptions/subscriptionsAsync';
// Actions
import { UsersActions } from 'store/users/usersSlice';
import { AuthActions } from 'store/auth/authSlice';
// Models
import { ISubscription } from 'models/Subscriptions';
// Types
import SubscriptionStatus from 'types/SubscriptionStatus';
// Services
import StorageService from 'services/StorageService';
// MUI
import { makeStyles } from '@mui/styles';
import {
  Box, Button, Card, Chip, Collapse, Divider, Grid, Tooltip, Typography
} from '@mui/material';
import {
  DoneAll as DoneAllIcon,
  Done as DoneIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
// Components
import ConfirmCancelSubscription from './ConfirmCancelSubscription';
import Collapsable from 'components/Collapsable';
// Utilities
import { formatCash } from 'utilities/Utilities';

type Props = {
  subscription: ISubscription,
};

const SubscriptionCard:React.FC<Props> = ({ subscription }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = () => setOpen(prev => !prev);

  const handleSignOut = () => {
    StorageService.removeCredential();
    dispatch(AuthActions.setAuthenticated(false));
    dispatch(UsersActions.removeCurrentUser());
  };

  const cancelSubscription = () => {
    setLoading(true);
    dispatch(SubscriptionsAsync.cancelSubscription(subscription.id))
      .unwrap()
      .then((data) => {
        dispatch(UsersActions.updateUserSubscriptions(data));
        if (subscription.status === SubscriptionStatus.Trial) handleSignOut();
        handleOpen();
      })
      .finally(() => setLoading(false))
  }

  const endTrialDate: boolean = dayjs(subscription.startDate).add(subscription.subscriptionPackage.trialPeriodDaysNumber, 'day') < dayjs();
  const endTrialDateDays = dayjs(subscription.startDate).add(subscription.subscriptionPackage.trialPeriodDaysNumber, 'day').diff(dayjs(), 'days');

  const [ extended, setExtended ] = useState<boolean>(false);

  const toggleExtended = () => setExtended(prev => !prev);

  return (
    <React.Fragment>
      <ConfirmCancelSubscription
        open={open} onClose={handleOpen}
        cancel={cancelSubscription}
        status={subscription.status}
        name={subscription.subscriptionPackage.name}
      />

      <Card sx={{ p: 2, borderRadius: 0, backgroundColor: extended ? 'rgba(0, 0, 0, 0.04)' : '#fff', borderBottom: '1px solid #eee', width: '100%' }}>
        <Collapsable onClick={toggleExtended}>
          <Grid container spacing={2}>
            <Grid item xs={10} md={4} sx={{ order: -1, alignSelf: 'center' }}>
              <Typography className={classes.title}>
                Package
              </Typography>
              <Typography className={classes.text}>
                {subscription.subscriptionPackage.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignSelf: 'center', justifyContent: 'space-between', flexDirection: { xs: 'row', sm: 'column' } }}>
              <Typography className={classes.title}>
                Start date
              </Typography>
              <Typography className={classes.text}>
                {dayjs(subscription.startDate).format('MMM DD YYYY')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={1} sx={{ display: 'flex', alignSelf: 'center', justifyContent: 'space-between', flexDirection: { xs: 'row', sm: 'column' } }}>
              <Typography className={classes.title}>
                Price
              </Typography>
              <Typography className={classes.text}>
                {formatCash(subscription.subscriptionPackage.price)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} alignSelf="center">
              <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, justifyContent: 'space-between', mt: {xs: 2, sm: 0} }}>
                <Typography className={classes.title}>Status</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {subscription.status === SubscriptionStatus.Cancelled && <Chip color={'error'} size="small" label="Cancelled" icon={<CloseIcon />} />}
                  {subscription.status === SubscriptionStatus.Active && (
                    <React.Fragment>
                      <Chip color={'success'} size="small" label="Active" icon={<DoneAllIcon />} />
                      {subscription.overdue ? (
                        <Typography
                          className={classes.title}
                          sx={{ color: '#d32f2f !important' }}
                        >
                          Overdue
                        </Typography>
                      ) : null}
                    </React.Fragment>
                  )}
                  {subscription.status === SubscriptionStatus.Trial && (
                    <React.Fragment>
                      <Chip color={'info'} size="small" label="Trial" icon={<DoneIcon />} />
                      <Typography
                        className={classes.title}
                        sx={{ color: `${endTrialDate ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)'} !important` }}
                      >
                        {endTrialDate ? 'Expired' : `Expires in ${endTrialDateDays} day(s)`}
                      </Typography>
                    </React.Fragment>
                  )}
                </Box>
              </Box>
            </Grid>
            {subscription.cancelledDate && subscription.status === SubscriptionStatus.Active && (
              <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignSelf: 'center', justifyContent: 'space-between', flexDirection: { xs: 'row', sm: 'column' } }}>
                <Typography className={classes.title} sx={{ color: '#d32f2f !important' }}>
                  Cancellation scheduled
                </Typography>
                <Typography className={classes.text}>
                  {dayjs(subscription.cancelledDate).format('MMM DD YYYY')}
                </Typography>
              </Grid>
            )}
            {(subscription.status === SubscriptionStatus.Trial || (subscription.status === SubscriptionStatus.Active && !subscription.cancelledDate)) && (
              <Grid item xs={2} alignSelf="center" sx={{ display: 'flex', justifyContent: 'flex-end', order: { xs: -1, md: 0 } }}>
                <Tooltip title="Cancel subscription">
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    disabled={loading}
                    onClick={handleOpen}
                  >
                    Cancel
                  </Button>
                </Tooltip>
              </Grid>
              )}
          </Grid>
        </Collapsable>
        <Collapse in={extended}>
          <Divider sx={{ p: 1 }} />
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sx={{ display: 'flex', alignSelf: 'center', justifyContent: 'space-between', flexDirection: { xs: 'row', sm: 'column' } }}>
              <Typography className={classes.title}>
                Billing period
              </Typography>
              <Typography className={classes.text}>
                Every 30 days
              </Typography>
            </Grid>
          </Grid>
        </Collapse>
      </Card>
    </React.Fragment>
  );
}

export default SubscriptionCard;

const useStyles = makeStyles({
  title: {
    fontSize: '12px',
    lineHeight: '166%',
    letterSpacing: '0.4px',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  text: {
    fontSize: '14px',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
});
